import { useState } from "react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  MessageSquare,
  User,
} from "lucide-react";
import { formatDateTime, formatDuration } from "@repo/utils";

interface Copsoq3Reponse {
  name: string;
  question: string;
  echelle: string;
  domaine: string;
  reponse: string;
  score: number;
}

interface Copsoq3Donnees {
  type: "form";
  template: "copsoq3";
  reponses: Record<string, Copsoq3Reponse>;
}

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface Copsoq3ResultsProps {
  donnees: Copsoq3Donnees;
  candidatInfo: CandidatInfo;
}

const ECHELLE_DESCRIPTIONS: Record<string, string> = {
  "Charge de travail": "quantité de travail à accomplir",
  "Rythme de travail": "vitesse à laquelle les tâches doivent être réalisées",
  "Exigences cognitives":
    "demandes faisant appel aux capacités cognitives de la personne",
  Prévisibilité: "moyens permettant d'éviter l'incertitude et l'insécurité",
  Reconnaissance:
    "valorisation par le management des efforts fournis au travail",
  "Justice organisationnelle": "équité dans le traitement des salariés",
  "Clarté des rôles":
    "compréhension qu'a la personne de sa place dans l'organisation (contenu des tâches, attentes à satisfaire et responsabilités qui lui incombent)",
  "Conflit de valeurs":
    "devoir agir en opposition avec ses valeurs professionnelles, sociales ou personnelles",
  "Qualité de leadership du responsable hiérarchique":
    "manière dont le supérieur hiérarchique direct exerce son autorité",
  "Soutien de la part du responsable hiérarchique":
    "perception qu'a la personne de la possibilité d'obtenir un soutien de son supérieur direct en cas de besoin",
  "Confiance entre les salariés et le management":
    "capacité des salariés à faire confiance au management et réciproquement",
  "Confiance et coopération entre collègues":
    "qualité des relations entre collègues et capacité à travailler ensemble",
  "Soutien de la part des collègues":
    "perception qu'a la personne de la possibilité d'obtenir un soutien de ses collègues en cas de besoin",
  "Marge de manœuvre":
    "degré auquel la personne peut influer sur différents aspects de son travail",
  "Possibilités d'épanouissement":
    "caractère stimulant des tâches réalisées et opportunités d'apprentissage qu'elles offrent, permettant un développement non seulement professionnel mais aussi personnel",
  "Santé auto évaluée":
    "perception par la personne de son état de santé général",
  Stress: "niveau de fréquence de stress ressenti par la personne",
  Epuisement:
    "niveau de fréquence d'épuisement physique et mental ressenti par la personne",
  "Exigences émotionnelles":
    "demandes faisant appel aux capacités émotionnelles de la personne",
  "Conciliation entre vie privée et vie professionnelle":
    "répercussions possibles du travail sur la vie privée ou familiale",
  "Insécurité professionnelle":
    "sécurité du statut d'emploi (risque de licenciement, certitude de retrouver un emploi)",
  "Sens du travail": "sens et importance perçus du travail réalisé",
  "Engagement dans l'entreprise":
    "degré auquel la personne se sent attaché à son entreprise/organisation",
  "Satisfaction au travail": "satisfaction ressentie à l'égard de son travail",
};

// Upper bounds (exclusive) for classes 1-4; class 5 is the remainder
const DOMAIN_CLASS_BOUNDS: Record<string, [number, number, number, number]> = {
  "Contraintes quantitatives": [12, 16, 21, 25],
  "Organisation et Management": [25, 34, 46, 55],
  "Relations entre collègues": [7, 10, 15, 18],
  Autonomie: [7, 9, 13, 16],
  "Santé et Bien-être": [20, 27, 37, 43],
  "Vécu professionnel": [6, 9, 13, 16],
};

function getDomainClass(domainName: string, totalBrut: number): number {
  const bounds = DOMAIN_CLASS_BOUNDS[domainName];
  if (!bounds) return 0;
  for (let i = 0; i < bounds.length; i++) {
    if (totalBrut < bounds[i]!) return i + 1;
  }
  return 5;
}

const DOMAINS_CONFIG = [
  {
    name: "Contraintes quantitatives",
    color: "#f97316",
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
  },
  {
    name: "Organisation et Management",
    color: "#3b82f6",
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
  },
  {
    name: "Relations entre collègues",
    color: "#22c55e",
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
  },
  {
    name: "Autonomie",
    color: "#a855f7",
    bg: "bg-purple-50",
    border: "border-purple-200",
    text: "text-purple-700",
  },
  {
    name: "Santé et Bien-être",
    color: "#ef4444",
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
  },
  {
    name: "Vécu professionnel",
    color: "#14b8a6",
    bg: "bg-teal-50",
    border: "border-teal-200",
    text: "text-teal-700",
  },
] as const;

const DIMENSIONS_ORDER: Record<string, string[]> = {
  "Contraintes quantitatives": [
    "Charge de travail",
    "Rythme de travail",
    "Exigences cognitives",
  ],
  "Organisation et Management": [
    "Prévisibilité",
    "Reconnaissance",
    "Justice organisationnelle",
    "Clarté des rôles",
    "Conflit de valeurs",
    "Qualité de leadership du responsable hiérarchique",
    "Soutien de la part du responsable hiérarchique",
    "Confiance entre les salariés et le management",
  ],
  "Relations entre collègues": [
    "Confiance et coopération entre collègues",
    "Soutien de la part des collègues",
  ],
  Autonomie: ["Marge de manœuvre", "Possibilités d'épanouissement"],
  "Santé et Bien-être": [
    "Santé auto évaluée",
    "Stress",
    "Epuisement",
    "Exigences émotionnelles",
    "Conciliation entre vie privée et vie professionnelle",
    "Insécurité professionnelle",
  ],
  "Vécu professionnel": [
    "Sens du travail",
    "Engagement dans l'entreprise",
    "Satisfaction au travail",
  ],
};

function getMaxScore(echelle: string, questionCount: number): number {
  if (echelle === "Satisfaction au travail") return 3;
  if (echelle === "Santé auto évaluée") return 4;
  return questionCount * 4;
}

function getMultiplier(echelle: string, questionCount: number): number {
  if (echelle === "Satisfaction au travail") return 33.33;
  if (echelle === "Santé auto évaluée") return 25;
  if (questionCount === 3) return 8.34;
  return 12.5;
}

const ZONE_COLORS = {
  red: "#ef4444",
  orange: "#eab308",
  green: "#22c55e",
} as const;

function getScoreZone(
  echelle: string,
  scoreBrut: number,
  questionCount: number,
): keyof typeof ZONE_COLORS {
  if (echelle === "Satisfaction au travail") {
    if (scoreBrut <= 1) return "red";
    if (scoreBrut <= 2) return "orange";
    return "green";
  }
  if (echelle === "Santé auto évaluée") {
    if (scoreBrut <= 1) return "red";
    if (scoreBrut <= 3) return "orange";
    return "green";
  }
  const max = questionCount * 4;
  if (max === 8) {
    if (scoreBrut <= 2) return "red";
    if (scoreBrut <= 5) return "orange";
    return "green";
  }
  // max === 12
  if (scoreBrut <= 3) return "red";
  if (scoreBrut <= 7) return "orange";
  return "green";
}

function GaussianBars({ classe, color }: { classe: number; color: string }) {
  const heights = [12, 34, 48, 34, 12];
  const barWidth = 22;
  const gap = 5;
  const maxHeight = 48;
  const svgWidth = heights.length * barWidth + (heights.length - 1) * gap;

  return (
    <div className="flex flex-col items-center">
      <svg width={svgWidth} height={maxHeight}>
        {heights.map((h, i) => (
          <rect
            key={i}
            x={i * (barWidth + gap)}
            y={maxHeight - h}
            width={barWidth}
            height={h}
            fill={color}
            opacity={i + 1 === classe ? 1 : 0.18}
            rx={4}
          />
        ))}
      </svg>
      <div className="flex mt-1" style={{ width: svgWidth }}>
        {[1, 2, 3, 4, 5].map((n, i) => (
          <div
            key={n}
            style={{
              width: i < 4 ? barWidth + gap : barWidth,
              textAlign: "center",
              fontSize: 10,
              fontWeight: n === classe ? 700 : 400,
              color: n === classe ? color : "#9ca3af",
            }}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReponsesDropdown({
  reponses,
}: {
  reponses: Record<string, Copsoq3Reponse>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const sortedReponses = Object.values(reponses).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  if (sortedReponses.length === 0) return null;

  return (
    <Card className="w-full">
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors select-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isOpen ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            )}
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">Réponses du candidat</CardTitle>
          </div>
          <Badge variant="outline">{sortedReponses.length} réponses</Badge>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-2 text-left font-medium text-muted-foreground w-12">
                  #
                </th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                  Question
                </th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground w-1/3">
                  Réponse
                </th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground w-16">
                  Score
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedReponses.map((rep) => (
                <tr
                  key={rep.name}
                  className="border-t border-border hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground align-top">
                    {rep.name}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p className="text-xs text-muted-foreground mb-0.5">
                      {rep.echelle} — {rep.domaine}
                    </p>
                    <p>{rep.question}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground align-top">
                    {rep.reponse}
                  </td>
                  <td className="px-4 py-3 text-right font-mono align-top">
                    {rep.score}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      )}
    </Card>
  );
}

export function Copsoq3Results({ donnees, candidatInfo }: Copsoq3ResultsProps) {
  if (donnees.type !== "form" || donnees.template !== "copsoq3") {
    return <div>Type de données incorrect</div>;
  }

  const domaineMap: Record<
    string,
    Record<string, { score: number; count: number }>
  > = {};

  for (const rep of Object.values(donnees.reponses)) {
    const domaine = rep.domaine;
    const echelle = rep.echelle;
    if (!domaineMap[domaine]) domaineMap[domaine] = {};
    const echelles = domaineMap[domaine]!;
    if (!echelles[echelle]) echelles[echelle] = { score: 0, count: 0 };
    echelles[echelle]!.score += rep.score;
    echelles[echelle]!.count++;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">COPSOQ 3</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Évaluation des risques psychosociaux
            </p>
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} — {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Présentation */}
      <div className="mb-6 px-4 md:px-6 space-y-3 text-sm text-muted-foreground max-w-full wrap-break-word">
        <h2 className="text-base font-semibold text-foreground">Présentation du COPSOQ</h2>
        <p>
          Le <span className="font-semibold text-foreground">COPSOQ</span>{" "}
          (Copenhagen Psychosocial Questionnaire) est un outil de référence en
          matière d'évaluation des facteurs de risques psychosociaux au travail.
          Elaboré par une équipe de chercheurs du Danish National Institute of
          Occupational Health de Copenhague au début des années 2000, le COPSOQ
          a ensuite été développé et validé dans de nombreuses langues au sein
          d'un réseau international.
        </p>
        <p>
          La version courte française du{" "}
          <span className="font-semibold text-foreground">COPSOQ III</span>{" "}
          comporte 54 items mesurant 24 facteurs de risque répartis dans 6
          domaines.
        </p>
      </div>

      {/* Domaines */}
      <div className="space-y-6">
        {DOMAINS_CONFIG.map((domainConfig) => {
          const echelles = domaineMap[domainConfig.name];
          if (!echelles) return null;

          const orderedDimensions =
            DIMENSIONS_ORDER[domainConfig.name] ?? Object.keys(echelles);

          const totalBrut = orderedDimensions.reduce((sum, echelle) => {
            const data = echelles[echelle];
            return sum + (data?.score ?? 0);
          }, 0);

          return (
            <Card
              key={domainConfig.name}
              className={`overflow-hidden border ${domainConfig.border}`}
            >
              <CardHeader className={`${domainConfig.bg} py-3 px-0`}>
                <div className="flex items-end w-full">
                  <div className="pl-4 pr-4 w-1/2">
                    <CardTitle className={`text-base ${domainConfig.text}`}>
                      {domainConfig.name}
                    </CardTitle>
                  </div>
                  <div className="flex items-end gap-3 px-4 w-1/2">
                    <span className={`text-sm ${domainConfig.text}`}>
                      Score étalonné :{" "}
                      <span className="font-semibold font-mono">
                        {getDomainClass(domainConfig.name, totalBrut)}/5
                      </span>
                    </span>
                    <GaussianBars
                      classe={getDomainClass(domainConfig.name, totalBrut)}
                      color={domainConfig.color}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Dimension
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground w-1/2">
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedDimensions.map((echelle) => {
                      const data = echelles[echelle];
                      if (!data) return null;

                      const maxScore = getMaxScore(echelle, data.count);
                      const multiplier = getMultiplier(echelle, data.count);
                      const scoreBrut = data.score;
                      const scoreNorm = Math.round(scoreBrut * multiplier);
                      const pct = (scoreBrut / maxScore) * 100;
                      const zone = getScoreZone(echelle, scoreBrut, data.count);
                      const description = ECHELLE_DESCRIPTIONS[echelle];

                      return (
                        <tr
                          key={echelle}
                          className="border-t border-border hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-2">
                            <p className="line-clamp-2 text-sm">
                              <span className="font-bold">{echelle}</span>
                              {description && (
                                <span className="text-muted-foreground ml-1.5">
                                  — {description}
                                </span>
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-2 w-1/2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-muted rounded-full h-2.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: ZONE_COLORS[zone],
                                  }}
                                />
                              </div>
                              <span
                                className="text-xs font-mono font-semibold w-9 shrink-0"
                                style={{ color: ZONE_COLORS[zone] }}
                              >
                                {scoreNorm}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Réponses */}
      <div className="mt-6">
        <ReponsesDropdown reponses={donnees.reponses} />
      </div>
    </div>
  );
}
